import { cleanText } from '@lib/customization/reportDesign';
import {
  brandingLayerKey, reportBrandingFieldKeys, reportBrandingTextKeys, type ReportBrandingFieldKey,
  type ReportBrandingFields, type ReportBrandingLayers, type ReportBrandingPatch, type ReportBrandingTextKey,
} from '@store/api/reportBrandingEndpoints';
import { type ReportImageRef, type ReportText } from '@store/api/reportDesignEndpoints';

/**
 * The pure half of the report branding page: what an unsaved draft of one layer is, what it
 * comes to as a request, and what the layer shows meanwhile. No React and no store, so every
 * rule here can be read, and run, by itself.
 */

/**
 * The unsaved changes to one layer, by field: a value to set, or `null` to take the field off
 * so the layer beneath shows through. A field that is not in it is as it was saved.
 *
 * A text is held as its input left it, blank locales and all. Whether it says anything is
 * decided where it is compared and where it is sent, never while it is being typed.
 */
export type ReportBrandingDraft = ReportBrandingPatch;

/**
 * The product's name, which the layouts print where no layer names a brand. Mirrors
 * `PRODUCT_NAME` in the backend's `report/pdf/assets.ts`.
 */
export const PRODUCT_NAME = 'SuPAR Insight';

type Translate = (key: string, values?: string[]) => string;

/**
 * What a layout prints for each text when no layer sets it, in the reader's language: the
 * values `CompactReport` and `ExecutiveReport` fall back to. The header's line is in
 * capitals there, cased in the language it is written in.
 */
export const builtInBrandTexts = (t: Translate, language: string): Record<ReportBrandingTextKey, string> => {
  const header = t('Inspection_Report');

  let headerLabel: string;

  try {
    headerLabel = header.toLocaleUpperCase(language);
  } catch (_) {
    // A language tag the runtime does not recognise: the language-neutral rules.
    headerLabel = header.toUpperCase();
  }

  return { brandName: PRODUCT_NAME, headerLabel, footerText: t('Report_Generated_With', [PRODUCT_NAME]) };
};

/** What a layer sets and nothing else about it. A layer that is not there sets nothing. */
export const fieldsOf = (layer: ReportBrandingFields | undefined): ReportBrandingFields => {
  const fields: ReportBrandingFields = {};

  for (const key of reportBrandingTextKeys) {
    if (layer?.[key]) fields[key] = layer[key];
  }

  if (layer?.logo) fields.logo = layer.logo;

  return fields;
};

const sameEntries = (a: ReportText | undefined, b: ReportText | undefined): boolean => {
  const left = Object.entries(a ?? {});
  const right = b ?? {};

  return left.length === Object.keys(right).length && left.every(([locale, value]) => right[locale] === value);
};

/** A text without the locales its input holds nothing in. A blank is kept: it is being typed. */
const withoutEmpty = (text: ReportText | null | undefined): ReportText =>
  Object.fromEntries(Object.entries(text ?? {}).filter(([, value]) => value !== ''));

const sameLogo = (a: ReportImageRef | null | undefined, b: ReportImageRef | null | undefined): boolean =>
  (a?.imageId ?? null) === (b?.imageId ?? null);

/** Whether a field of a draft comes to the request the saved layer would: saving it would change nothing. */
const changesNothing = (draft: ReportBrandingDraft, saved: ReportBrandingFields, key: ReportBrandingFieldKey): boolean =>
  (key === 'logo'
    ? sameLogo(draft.logo, saved.logo)
    : sameEntries(cleanText(draft[key] ?? undefined), cleanText(saved[key])));

/**
 * A draft after one of its texts was edited, or taken off with `null`. A value that is the
 * saved one again leaves the draft, so a change undone is no change. It is compared as it was
 * typed: a text that differs from the saved one only by blanks stays, or the blank just typed
 * would vanish from its box, and `pendingOf` is what says it is nothing to save.
 */
export const stageText = (
  draft: ReportBrandingDraft, saved: ReportBrandingFields, key: ReportBrandingTextKey, value: ReportText | null,
): ReportBrandingDraft => {
  const next: ReportBrandingDraft = { ...draft, [key]: value };

  if (sameEntries(withoutEmpty(value), withoutEmpty(saved[key]))) delete next[key];

  return next;
};

/** A draft after its logo was chosen, or taken off with `null`. The saved one again leaves the draft. */
export const stageLogo = (
  draft: ReportBrandingDraft, saved: ReportBrandingFields, value: ReportImageRef | null,
): ReportBrandingDraft => {
  const next: ReportBrandingDraft = { ...draft, logo: value };

  if (sameLogo(value, saved.logo)) delete next.logo;

  return next;
};

/**
 * What of a draft is still to be saved: the fields whose request differs from the saved
 * layer's. Whatever somebody else saved meanwhile, and whatever was typed and came to
 * nothing, is not.
 */
export const pendingOf = (draft: ReportBrandingDraft, saved: ReportBrandingFields): ReportBrandingDraft => {
  const pending: ReportBrandingDraft = { ...draft };

  for (const key of reportBrandingFieldKeys) {
    if (pending[key] === undefined || changesNothing(pending, saved, key)) delete pending[key];
  }

  return pending;
};

/** The layer as its inputs show it: what is saved, with the draft over it. Texts are as they were typed. */
export const applyDraft = (saved: ReportBrandingFields, draft: ReportBrandingDraft): ReportBrandingFields => {
  const shown: ReportBrandingFields = { ...saved };

  for (const key of reportBrandingTextKeys) {
    const text = draft[key];

    if (text === null) delete shown[key];
    else if (text !== undefined) shown[key] = text;
  }

  if (draft.logo === null) delete shown.logo;
  else if (draft.logo !== undefined) shown.logo = draft.logo;

  return shown;
};

/** Whether a field of the layer as shown says anything: a text with nothing but blanks in it does not. */
export const isFieldSet = (shown: ReportBrandingFields, key: ReportBrandingFieldKey): boolean =>
  (key === 'logo' ? Boolean(shown.logo) : cleanText(shown[key]) !== undefined);

/**
 * The layer as a request carries it whole, which is the `draft` of a preview: only the
 * fields that say something, and of a text only the locales that do.
 */
export const toBrandingBody = (shown: ReportBrandingFields): ReportBrandingFields => {
  const body: ReportBrandingFields = {};

  for (const key of reportBrandingTextKeys) {
    const text = cleanText(shown[key]);

    if (text) body[key] = text;
  }

  if (shown.logo) body.logo = { imageId: shown.logo.imageId };

  return body;
};

/**
 * What is still to be saved as the merge patch that saves it: a value sets the field and
 * `null` takes it off. A text left with nothing in it is sent as `null`, which is what the
 * server would make of it.
 */
export const toBrandingPatch = (pending: ReportBrandingDraft): ReportBrandingPatch => {
  const patch: ReportBrandingPatch = {};

  for (const key of reportBrandingTextKeys) {
    if (pending[key] !== undefined) patch[key] = cleanText(pending[key] ?? undefined) ?? null;
  }

  if (pending.logo !== undefined) patch.logo = pending.logo && { imageId: pending.logo.imageId };

  return patch;
};

/**
 * A draft once `sent` has been saved: what was sent is gone from it, and a field changed
 * while the request was on its way stays, compared by identity as the draft holds a new
 * value for every edit.
 */
export const withoutSent = (draft: ReportBrandingDraft, sent: ReportBrandingDraft): ReportBrandingDraft => {
  const left: ReportBrandingDraft = { ...draft };

  for (const key of reportBrandingFieldKeys) {
    if (key in sent && left[key] === sent[key]) delete left[key];
  }

  return left;
};

/** A brand as its layers come to, with the owner its logo is read from. */
export type ReaderBrand = ReportBrandingFields & { logoOwner: string | null };

/**
 * The brand a reader's own reports carry, from the layers they may read: their default
 * team's fields over the organization's, a field at a time and whole, as the server puts
 * them together for a report. For the design editor's canvas, which draws a design's logo
 * and brand name from it where the design sets none.
 *
 * Only as good as what the reader may read. Someone whose default team's layer is not theirs
 * to read gets the organization's here, where the report they download carries that team's.
 */
export const readerBrand = (layers: ReportBrandingLayers | undefined, defaultTeamId: string | undefined): ReaderBrand => {
  const brand: ReaderBrand = { logoOwner: null };

  for (const teamId of defaultTeamId ? [null, defaultTeamId] : [null]) {
    const layer = fieldsOf(layers?.[brandingLayerKey({ teamId })]);

    for (const key of reportBrandingTextKeys) {
      if (layer[key]) brand[key] = layer[key];
    }

    if (layer.logo) {
      brand.logo = layer.logo;
      brand.logoOwner = teamId;
    }
  }

  return brand;
};
